import type { AdditionalFees, FeeProfile, FulfillmentBracket, InboundPlacementMode, ReferralTier } from './types';

function tierFee(price: number, tiers: ReferralTier[]): number {
  let fee = 0;
  let previousCap = 0;

  for (const tier of tiers) {
    const cap = tier.up_to;
    const upperBound = cap === null ? price : Math.min(price, cap);
    const taxableAmount = Math.max(0, upperBound - previousCap);
    fee += taxableAmount * (tier.rate || 0);

    if (cap === null || price <= cap) {
      break;
    }

    previousCap = cap;
  }

  return fee;
}

function resolveFulfillmentTableKey(profile: FeeProfile, category: string, price: number): keyof FeeProfile['fulfillmentFees'] {
  const threshold = typeof profile.lowPriceThreshold === 'number' ? profile.lowPriceThreshold : 10;
  const lowPrice = price < threshold;
  const isApparel = profile.apparelCategories.includes(category);

  if (lowPrice) {
    return isApparel ? 'low_price_apparel' : 'low_price_non_apparel';
  }
  return isApparel ? 'apparel' : 'non_apparel';
}

function resolveBracketCost(weight: number, bracket: FulfillmentBracket): number {
  if (typeof bracket.cost === 'number') return bracket.cost;
  const cost = bracket.cost;
  if (weight <= cost.threshold) return cost.base;
  const over = weight - cost.threshold;
  const intervals = Math.ceil(over / cost.interval);
  return cost.base + intervals * cost.extra;
}

export function getFulfillmentFee(profile: FeeProfile, category: string, sizeTier: string, weight: number, price: number): number {
  const key = resolveFulfillmentTableKey(profile, category, price);
  const table = profile.fulfillmentFees[key][sizeTier];
  if (!table) return 0;

  for (const bracket of table) {
    const min = bracket.min ?? 0;
    const max = bracket.max;
    const withinMin = weight >= min;
    const withinMax = max === null || weight <= max;
    if (!withinMin || !withinMax) continue;
    return resolveBracketCost(weight, bracket);
  }

  return 0;
}

export function getReferralFee(profile: FeeProfile, category: string, price: number): number {
  const row =
    profile.referralFees.find((item) => item.category === category) ||
    profile.referralFees.find((item) => item.category === 'Everything Else');

  if (!row) return 0;

  const minFee = row.min_fee ?? 0;
  const fee = tierFee(price, row.tiers || []);
  return Math.max(fee, minFee);
}

export function getVariableClosingFee(profile: FeeProfile, category: string): number {
  const cfg = profile.additionalFees.variableClosing;
  if (!cfg) return 0;
  return cfg.categories.includes(category) ? cfg.per_item : 0;
}

export function getFulfillmentSurchargeFee(profile: FeeProfile, fulfillmentFee: number): number {
  const cfg = profile.additionalFees.fulfillmentSurcharge;
  if (!cfg || !Number.isFinite(cfg.rate)) return 0;
  return Math.max(0, fulfillmentFee) * Math.max(0, cfg.rate);
}

export function getInboundPlacementFee(
  profile: FeeProfile,
  strategy: InboundPlacementMode,
  manualFee: number,
  sizeTier: string
): number {
  if (strategy === 'optimized') return 0;
  const manual = Number.isFinite(manualFee) ? Math.max(0, manualFee) : 0;
  if (strategy === 'manual') return manual;

  if (strategy === 'minimal') {
    if (manual > 0) return manual;
    const est = profile.additionalFees.inboundPlacementEstimates || {};
    return est[sizeTier] || 0;
  }

  return 0;
}

export function getStorageFees(profile: FeeProfile, sizeTier: string, length: number, width: number, height: number) {
  const volumeFt = (length * width * height) / 1728;
  const isStandard = sizeTier === 'Small standard' || sizeTier === 'Large standard';
  const type = isStandard ? 'standard' : 'oversize';

  return {
    offSeason: volumeFt * profile.storageRates.offSeason[type],
    peak: volumeFt * profile.storageRates.peak[type]
  };
}

export function getExpectedReturnsCost(profile: FeeProfile, category: string, fulfillmentFee: number, referralFee: number) {
  const cfg: NonNullable<AdditionalFees['expectedReturns']> = {
    defaultRate: 0.04,
    highReturnRate: 0.1,
    highReturnCategories: [],
    processingFeeMultiplier: 1,
    refundAdminRate: 0.2,
    refundAdminCap: 5,
    ...profile.additionalFees.expectedReturns
  };

  const rate = cfg.highReturnCategories.includes(category) ? cfg.highReturnRate : cfg.defaultRate;
  const returnsProcessingFee = Math.max(0, fulfillmentFee * cfg.processingFeeMultiplier);
  const refundAdminFee = Math.min(cfg.refundAdminCap, Math.max(0, referralFee * cfg.refundAdminRate));
  const singleReturnCost = returnsProcessingFee + refundAdminFee;

  return {
    expectedCostPerUnit: singleReturnCost * rate,
    singleReturnCost
  };
}

export function getLowInventoryLevelFee(profile: FeeProfile, sizeTier: string, weight: number): number {
  const cfg = profile.additionalFees.lowInventoryLevel;
  if (!cfg) return 0;
  const isStandard = sizeTier === 'Small standard' || sizeTier === 'Large standard';
  if (!isStandard) return 0;

  const extraPounds = Math.max(0, Math.ceil(weight - 1));
  return cfg.standardBaseFee + extraPounds * cfg.extraPerPoundAboveOne;
}

export function getAgedInventorySurchargeRange(profile: FeeProfile, length: number, width: number, height: number) {
  const cfg = profile.additionalFees.agedInventorySurcharge;
  if (!cfg) return { minMonthly: 0, maxMonthly: 0 };

  const volumeFt = (length * width * height) / 1728;
  return {
    minMonthly: volumeFt * cfg.minPerCubicFoot,
    maxMonthly: volumeFt * cfg.maxPerCubicFoot
  };
}
